/**
 * Shared i18n setup for /app, /microsite and /scanner.
 *
 * Call `initI18n()` once at boot. It keeps <html lang> in sync, which matters
 * beyond accessibility here: shared/styles/tokens.css scopes the Greek
 * uppercase override to html[lang='el'].
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import el from './el.json';
import en from './en.json';

export const SUPPORTED_LOCALES = ['el', 'en'];
export const DEFAULT_LOCALE = 'el';
const STORAGE_KEY = 'hba.locale';

function detectLocale() {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  } catch {
    // Private-mode Safari throws on localStorage access; fall through.
  }

  // Deliberately NOT sniffing navigator.language. This is a Greek banking
  // event: plenty of attendees run their phone in English, and serving them an
  // English site by default would be the wrong guess for the majority. Greek is
  // the default until someone explicitly chooses otherwise, and that choice is
  // then remembered.
  return DEFAULT_LOCALE;
}

export function applyDocumentLocale(locale) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
}

export function setLocale(locale) {
  if (!SUPPORTED_LOCALES.includes(locale)) return;
  i18n.changeLanguage(locale);
  applyDocumentLocale(locale);
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    // Non-persistent locale is an acceptable degradation.
  }
}

export function getLocale() {
  return i18n.resolvedLanguage || DEFAULT_LOCALE;
}

export function initI18n() {
  if (i18n.isInitialized) return i18n;

  const lng = detectLocale();

  i18n.use(initReactI18next).init({
    lng,
    // Greek is the source language: a missing English string shows Greek rather
    // than a raw key, which is the right failure mode for a Greek audience.
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    resources: {
      el: { translation: el },
      en: { translation: en },
    },
    interpolation: { escapeValue: false },
    returnEmptyString: false,
  });

  applyDocumentLocale(lng);
  return i18n;
}

export default i18n;
